import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { translations } from '../translations/en';

interface VolumeSelectorProps {
  onVolumeSelect: (level: number) => void;
  selectedVolume: number | null;
  ear: 'left' | 'right';
}

export const VolumeSelector: React.FC<VolumeSelectorProps> = ({
  onVolumeSelect,
  selectedVolume,
  ear,
}) => {
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstButtonRef.current?.focus();
  }, []);

  const handleButtonClick = (level: number) => {
    onVolumeSelect(level);
  };

  const getButtonSize = (level: number) => {
    const sizes: Record<number, string> = {
      1: 'w-8 h-8 text-base',
      2: 'w-9 h-9 text-lg',
      3: 'w-10 h-10 text-xl',
      4: 'w-11 h-11 text-xl',
      5: 'w-12 h-12 text-2xl',
      6: 'w-12 h-12 text-2xl',
      7: 'w-[3.2rem] h-[3.2rem] text-[1.6rem]',
      8: 'w-[3.4rem] h-[3.4rem] text-[1.7rem]',
      9: 'w-[3.6rem] h-[3.6rem] text-[1.8rem]',
      10: 'w-[3.8rem] h-[3.8rem] text-[1.9rem]',
      11: 'w-16 h-16 text-[2rem]',
      12: 'w-[4.2rem] h-[4.2rem] text-[2rem]',
    };
    return sizes[level] || 'w-8 h-8 text-base';
  };

  const isLeftEar = ear === 'left';

  return (
    <div
      className={classNames(
        'relative flex w-full flex-col pb-8 lg:flex-row lg:items-center lg:justify-center lg:pb-40',
        {
          'items-start': isLeftEar,
          'items-end': !isLeftEar,
        }
      )}
    >
      {Array.from({ length: 12 }, (_, i) => i + 1).map((level) => {
        const isSelected = selectedVolume === level;
        const isFirstButton = level === 1;
        const hasSelection = selectedVolume !== null;

        return (
          <div key={level} className="relative overflow-visible" data-val={level}>
            {isFirstButton && !hasSelection && (
              <div
                className={classNames(
                  'absolute bottom-full flex flex-col items-center lg:left-1/2 lg:-translate-x-1/2',
                  {
                    'left-0': isLeftEar,
                    'right-0': !isLeftEar,
                  }
                )}
              >
                <div className="mb-1 font-bold">{translations.start}</div>
                <svg
                  width="16"
                  height="23"
                  viewBox="0 0 16 23"
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  aria-label={translations.arrowpointingstart}
                >
                  <path
                    d="M10.752 14.616h4.928l-7.84 7.84L0 14.616h4.593V0h6.16v14.616z"
                    fill="#ffd100"
                    fillRule="evenodd"
                  />
                </svg>
              </div>
            )}

            <button
              ref={isFirstButton ? firstButtonRef : null}
              className={classNames(
                'relative z-10 mx-2 my-4 rounded-full border-none font-light transition-colors',
                getButtonSize(level),
                {
                  'bg-[#545859] after:absolute after:left-0 after:top-0 after:-z-10 after:h-full after:w-full after:animate-pulse-ring after:rounded-full after:bg-[#545859]':
                    isSelected,
                  'bg-[#ffd100] hover:cursor-pointer hover:bg-[#545859]': !isSelected,
                }
              )}
              data-val={level}
              onClick={() => handleButtonClick(level)}
              aria-label={translations.volumelevel.replace('{level}', level.toString())}
              aria-pressed={isSelected}
            >
              <span
                className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${
                  isSelected ? 'text-white' : ''
                }`}
              >
                {level}
              </span>
            </button>

            {isSelected && (
              <div
                className={classNames(
                  'absolute top-1/2 z-30 w-48 rounded-lg bg-gray-800 p-4 text-white shadow-lg lg:left-1/2 lg:top-full lg:mt-6 lg:-translate-x-1/2 lg:translate-y-0',
                  isLeftEar
                    ? 'left-full ml-4 -translate-y-1/2 lg:translate-y-0'
                    : 'right-full mr-4 -translate-y-1/2 lg:translate-y-0'
                )}
              >
                <p className="mb-2">
                  <strong className="mb-1 block">{translations.heartone}</strong>
                  <span className="text-sm">{translations.heartone2}</span>
                </p>
                <p>
                  <strong className="mb-1 block">{translations.barelyhear}</strong>
                  <span className="text-sm">{translations.clicksave}</span>
                </p>
                <div
                  className={classNames(
                    'absolute top-1/2 h-0 w-0 -translate-y-1/2 border-8 border-transparent lg:-top-2 lg:left-1/2 lg:-translate-x-1/2 lg:translate-y-0 lg:border-b-8 lg:border-l-8 lg:border-r-8 lg:border-t-0 lg:border-b-gray-800 lg:border-l-transparent lg:border-r-transparent',
                    isLeftEar ? 'right-full border-r-gray-800' : 'left-full border-l-gray-800'
                  )}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
